import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Checkbox,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Avatar,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Check as CompleteIcon,
  OpenInNew as OpenIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material'
import LoadingSpinner from '../components/LoadingSpinner'
import PageHelpButton from '../components/PageHelpButton'
import { colors } from '../theme'
import { tasksApi, Task, clientsApi, Client } from '../services/api'
import { useNotification } from '../hooks/useNotification'
import { useWebSocket } from '../hooks/useWebSocket'
import { STAGE_LABELS } from '../state/clients'

const priorityColors: Record<string, 'error' | 'warning' | 'info'> = {
  high: 'error',
  medium: 'warning',
  low: 'info',
}

export default function Tasks() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [tabValue, setTabValue] = useState(0)
  const [tasks, setTasks] = useState<Task[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; task: Task } | null>(null)
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [clients, setClients] = useState<Client[]>([])
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium' as Task['priority'],
    dueDate: '',
    clientId: '',
    clientName: '',
  })
  const { showSuccess, showError } = useNotification()
  const isInitialLoad = useRef(true)

  const loadTasks = useCallback(async () => {
    try {
      if (isInitialLoad.current) setLoading(true)
      setError(null)
      const tasks = await tasksApi.list({ all: true })
      setTasks(tasks ?? [])
    } catch (err) {
      console.error('Error loading tasks:', err)
      setError(err instanceof Error ? err.message : 'Failed to load tasks')
    } finally {
      setLoading(false)
      isInitialLoad.current = false
    }
  }, [])

  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  useWebSocket({
    task_created: () => loadTasks(),
    task_updated: () => loadTasks(),
    task_completed: () => loadTasks(),
    task_deleted: () => loadTasks(),
    task_assigned: () => loadTasks(),
    // MWA-390 — catch up on anything missed while the socket was down.
    ws_reconnected: () => loadTasks(),
  })

  useEffect(() => {
    if (addDialogOpen && clients.length === 0) {
      clientsApi.list({ all: true }).then(setClients).catch(() => {})
    }
  }, [addDialogOpen])

  const handleToggleComplete = async (task: Task) => {
    try {
      if (task.status === 'completed') {
        await tasksApi.update({ id: task.id, status: 'pending' })
        showSuccess('Task marked as pending')
      } else {
        await tasksApi.complete(task.id)
        showSuccess('Task completed')
      }
      loadTasks()
    } catch (err) {
      console.error('Error updating task:', err)
      showError(err instanceof Error ? err.message : 'Failed to update task')
    }
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, task: Task) => {
    setMenuAnchor({ el: event.currentTarget, task })
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const handleStatusChange = async (status: Task['status']) => {
    if (!menuAnchor) return
    try {
      await tasksApi.update({ id: menuAnchor.task.id, status })
      showSuccess(`Task marked as ${status.replace('_', ' ')}`)
      loadTasks()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to update task')
    }
    handleMenuClose()
  }

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      showError('Task title is required')
      return
    }
    try {
      setCreating(true)
      await tasksApi.create({
        ...newTask,
        type: 'follow_up',
        clientId: newTask.clientId || undefined,
        clientName: newTask.clientName || undefined,
        dueDate: newTask.dueDate || undefined,
      })
      showSuccess('Task created successfully')
      setAddDialogOpen(false)
      setNewTask({ title: '', description: '', priority: 'medium', dueDate: '', clientId: '', clientName: '' })
      loadTasks()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to create task')
    } finally {
      setCreating(false)
    }
  }

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch =
      task.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.clientName?.toLowerCase().includes(searchQuery.toLowerCase())

    if (!matchesSearch) return false

    if (tabValue === 0) return task.status !== 'completed'
    if (tabValue === 1) return task.status === 'completed'
    return true
  })

  // Split into custom and gate tasks
  const customTasks = filteredTasks.filter((task) => !task.isGateTask)
  const gateTasks = filteredTasks.filter((task) => task.isGateTask === true)

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const isOverdue = (dateStr?: string, status?: string) => {
    if (!dateStr || status === 'completed') return false
    const date = new Date(dateStr)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return date < today
  }

  const isDueSoon = (dateStr?: string, status?: string) => {
    if (!dateStr || status === 'completed') return false
    const date = new Date(dateStr)
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    return date <= tomorrow && !isOverdue(dateStr, status)
  }

  const getStageLabel = (stageId?: string) => {
    if (!stageId) return '-'
    return (STAGE_LABELS as Record<string, string>)[stageId] || stageId.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  // Stats
  const stats = {
    total: tasks.length,
    active: tasks.filter((t) => t.status !== 'completed').length,
    completed: tasks.filter((t) => t.status === 'completed').length,
    overdue: tasks.filter((t) => isOverdue(t.dueDate, t.status)).length,
  }

  if (loading) {
    return <LoadingSpinner />
  }

  // Shared row styling for overdue/due-soon
  const getRowSx = (task: Task) => ({
    backgroundColor: isOverdue(task.dueDate, task.status)
      ? 'error.lighter'
      : isDueSoon(task.dueDate, task.status)
        ? 'warning.lighter'
        : 'inherit',
    '&:hover': {
      backgroundColor: isOverdue(task.dueDate, task.status)
        ? 'error.light'
        : isDueSoon(task.dueDate, task.status)
          ? 'warning.light'
          : 'action.hover',
    },
  })

  const getMobileCardSx = (task: Task) => ({
    borderColor: isOverdue(task.dueDate, task.status)
      ? 'error.main'
      : isDueSoon(task.dueDate, task.status)
        ? 'warning.main'
        : 'divider',
    borderWidth: isOverdue(task.dueDate, task.status) || isDueSoon(task.dueDate, task.status) ? 2 : 1,
    backgroundColor: isOverdue(task.dueDate, task.status)
      ? 'error.lighter'
      : isDueSoon(task.dueDate, task.status)
        ? 'warning.lighter'
        : 'background.paper',
  })

  const renderDueDate = (task: Task) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {isOverdue(task.dueDate, task.status) && (
        <Tooltip title="Overdue">
          <WarningIcon color="error" fontSize="small" />
        </Tooltip>
      )}
      <Typography
        variant="body2"
        color={
          isOverdue(task.dueDate, task.status)
            ? 'error'
            : isDueSoon(task.dueDate, task.status)
              ? 'warning.dark'
              : 'text.primary'
        }
        fontWeight={isOverdue(task.dueDate, task.status) ? 600 : 400}
      >
        {formatDate(task.dueDate)}
      </Typography>
    </Box>
  )

  const renderTaskCell = (task: Task) => (
    <TableCell>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={{
          textDecoration: task.status === 'completed' ? 'line-through' : 'none',
          color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
        }}
      >
        {task.title}
      </Typography>
      {task.description && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {task.description.substring(0, 60)}
          {task.description.length > 60 ? '...' : ''}
        </Typography>
      )}
    </TableCell>
  )

  const renderClientCell = (task: Task) => (
    <TableCell>
      {task.clientId ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: colors.primary }}>
            {task.clientName?.[0] || '?'}
          </Avatar>
          <Link
            component="button"
            variant="body2"
            onClick={() => navigate(`/clients/${task.clientId}?tab=tasks`)}
            sx={{ textAlign: 'left' }}
          >
            {task.clientName || 'Unknown'}
          </Link>
          <IconButton
            size="small"
            onClick={() => navigate(`/clients/${task.clientId}?tab=tasks`)}
            sx={{ ml: 'auto' }}
          >
            <OpenIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">No client</Typography>
      )}
    </TableCell>
  )

  // Render mobile card section
  const renderMobileSection = (sectionTasks: Task[], isGate: boolean) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {sectionTasks.map((task) => (
        <Card key={task.id} variant="outlined" sx={getMobileCardSx(task)}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
              <Checkbox
                checked={task.status === 'completed'}
                onChange={() => handleToggleComplete(task)}
                size="small"
                sx={{ mt: -0.5 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      textDecoration: task.status === 'completed' ? 'line-through' : 'none',
                      color: task.status === 'completed' ? 'text.secondary' : 'text.primary',
                    }}
                  >
                    {task.title}
                  </Typography>
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e, task)} sx={{ ml: 1 }}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>
                {task.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mb: 1 }}>
                    {task.description.substring(0, 80)}
                    {task.description.length > 80 ? '...' : ''}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                  {isGate && task.stageId && (
                    <Chip
                      label={getStageLabel(task.stageId)}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ fontSize: '0.6875rem', height: 20 }}
                    />
                  )}
                  {!isGate && (
                    <Chip
                      label={task.priority}
                      size="small"
                      color={priorityColors[task.priority] || 'default'}
                      sx={{ fontSize: '0.6875rem', height: 20, textTransform: 'capitalize' }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {task.clientId && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Avatar sx={{ width: 20, height: 20, fontSize: '0.65rem', bgcolor: colors.primary }}>
                    {task.clientName?.[0] || '?'}
                  </Avatar>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate(`/clients/${task.clientId}?tab=tasks`)}
                    sx={{ fontSize: '0.8125rem', textAlign: 'left' }}
                  >
                    {task.clientName || 'Unknown'}
                  </Link>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/clients/${task.clientId}?tab=tasks`)}
                    sx={{ ml: 'auto', p: 0.5 }}
                  >
                    <OpenIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
              {task.dueDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  {isOverdue(task.dueDate, task.status) && (
                    <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.8125rem',
                      color: isOverdue(task.dueDate, task.status)
                        ? 'error.main'
                        : isDueSoon(task.dueDate, task.status)
                          ? 'warning.dark'
                          : 'text.secondary',
                      fontWeight: isOverdue(task.dueDate, task.status) ? 600 : 400,
                    }}
                  >
                    {formatDate(task.dueDate)}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  )

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, sm: 3 }, flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Tasks</Typography>
          <PageHelpButton articleId="tasks-types-explained" />
        </Stack>
        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
            onClick={loadTasks}
            disabled={loading}
            size={isMobile ? 'small' : 'medium'}
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
            <RefreshIcon sx={{ display: { xs: 'inline-block', sm: 'none' } }} />
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'inline-block' } }} />}
            onClick={() => setAddDialogOpen(true)}
            size={isMobile ? 'small' : 'medium'}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Task</Box>
            <AddIcon sx={{ display: { xs: 'inline-block', sm: 'none' } }} />
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mb: { xs: 2, sm: 3 }, flexWrap: 'wrap' }}>
        <Card sx={{ flex: 1, minWidth: { xs: 'calc(50% - 4px)', sm: 120 } }}>
          <CardContent sx={{ py: { xs: 1.25, sm: 1.5 }, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
            <Typography variant="h4" color="primary" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              {stats.active}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Active Tasks
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, minWidth: { xs: 'calc(50% - 4px)', sm: 120 } }}>
          <CardContent sx={{ py: { xs: 1.25, sm: 1.5 }, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
            <Typography variant="h4" color="success.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
              {stats.completed}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
              Completed
            </Typography>
          </CardContent>
        </Card>
        {stats.overdue > 0 && (
          <Card sx={{ flex: 1, minWidth: { xs: 'calc(50% - 4px)', sm: 120 }, borderColor: 'error.main', borderWidth: 2 }}>
            <CardContent sx={{ py: { xs: 1.25, sm: 1.5 }, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
              <Typography variant="h4" color="error.main" sx={{ fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
                {stats.overdue}
              </Typography>
              <Typography variant="body2" color="error.main" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Overdue
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Search */}
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <TextField
              placeholder="Search tasks or clients..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>

          {/* Tabs */}
          <Tabs
            value={tabValue}
            onChange={(_, v) => setTabValue(v)}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
            sx={{
              mb: 2,
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                minWidth: { xs: 'auto', sm: 'auto' },
                px: { xs: 1.5, sm: 2 },
              },
            }}
          >
            <Tab label={`Active (${stats.active})`} />
            <Tab label={`Completed (${stats.completed})`} />
          </Tabs>

          {/* Tasks Sections */}
          {filteredTasks.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                {tasks.length === 0 ? 'No tasks yet' : 'No tasks match your criteria'}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Custom Tasks Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                  Custom Tasks ({customTasks.length})
                </Typography>
                {customTasks.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No custom tasks {tabValue === 0 ? 'active' : 'completed'}
                    </Typography>
                  </Paper>
                ) : isMobile ? (
                  renderMobileSection(customTasks, false)
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell padding="checkbox" />
                          <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 180 }}>Client</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 100 }}>Priority</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 110 }}>Due Date</TableCell>
                          <TableCell sx={{ width: 50 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customTasks.map((task) => (
                          <TableRow key={task.id} hover sx={getRowSx(task)}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={task.status === 'completed'}
                                onChange={() => handleToggleComplete(task)}
                                size="small"
                              />
                            </TableCell>
                            {renderTaskCell(task)}
                            {renderClientCell(task)}
                            <TableCell>
                              <Chip
                                label={task.priority}
                                size="small"
                                color={priorityColors[task.priority] || 'default'}
                                sx={{ fontSize: 11, height: 22, textTransform: 'capitalize' }}
                              />
                            </TableCell>
                            <TableCell>{renderDueDate(task)}</TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={(e) => handleMenuOpen(e, task)}>
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>

              {/* Stage Gate Tasks Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>
                  Stage Gate Tasks ({gateTasks.length})
                </Typography>
                {gateTasks.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      No stage gate tasks {tabValue === 0 ? 'active' : 'completed'}
                    </Typography>
                  </Paper>
                ) : isMobile ? (
                  renderMobileSection(gateTasks, true)
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.50' }}>
                          <TableCell padding="checkbox" />
                          <TableCell sx={{ fontWeight: 600 }}>Task</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 180 }}>Client</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 160 }}>Stage</TableCell>
                          <TableCell sx={{ fontWeight: 600, width: 110 }}>Due Date</TableCell>
                          <TableCell sx={{ width: 50 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {gateTasks.map((task) => (
                          <TableRow key={task.id} hover sx={getRowSx(task)}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={task.status === 'completed'}
                                onChange={() => handleToggleComplete(task)}
                                size="small"
                              />
                            </TableCell>
                            {renderTaskCell(task)}
                            {renderClientCell(task)}
                            <TableCell>
                              <Chip
                                label={getStageLabel(task.stageId)}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ fontSize: 11, height: 22 }}
                              />
                            </TableCell>
                            <TableCell>{renderDueDate(task)}</TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={(e) => handleMenuOpen(e, task)}>
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Task Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Task</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Title"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Description"
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Client</InputLabel>
              <Select
                value={newTask.clientId}
                label="Client"
                onChange={(e) => {
                  const client = clients.find((c) => c.id === e.target.value)
                  setNewTask({ ...newTask, clientId: e.target.value as string, clientName: client?.name || '' })
                }}
              >
                <MenuItem value="">None</MenuItem>
                {clients.map((client) => (
                  <MenuItem key={client.id} value={client.id}>
                    {client.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Priority</InputLabel>
                <Select
                  value={newTask.priority}
                  label="Priority"
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as Task['priority'] })}
                >
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Due Date"
                type="date"
                value={newTask.dueDate}
                onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAddDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateTask} disabled={creating}>
            {creating ? <CircularProgress size={24} color="inherit" /> : 'Create Task'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu anchorEl={menuAnchor?.el} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        {menuAnchor?.task.status !== 'in_progress' && (
          <MenuItem onClick={() => handleStatusChange('in_progress')}>
            <ListItemIcon>
              <StartIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Start</ListItemText>
          </MenuItem>
        )}
        {menuAnchor?.task.status !== 'completed' && (
          <MenuItem onClick={() => handleStatusChange('completed')}>
            <ListItemIcon>
              <CompleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Complete</ListItemText>
          </MenuItem>
        )}
        {menuAnchor?.task.status === 'completed' && (
          <MenuItem onClick={() => handleStatusChange('pending')}>
            <ListItemIcon>
              <RefreshIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Reopen</ListItemText>
          </MenuItem>
        )}
        {menuAnchor?.task.clientId && (
          <MenuItem onClick={() => {
            navigate(`/clients/${menuAnchor.task.clientId}?tab=tasks`)
            handleMenuClose()
          }}>
            <ListItemIcon>
              <OpenIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Client Tasks</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
